/**
 * Articles Page
 *
 * Mobile-first article list with cards on mobile, table on desktop.
 */

import { useState, useCallback, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search, Package, ChevronRight, AlertTriangle, Pencil, ArrowUp, ArrowDown, Trash2, RotateCcw, Copy, ScanBarcode, Tags } from 'lucide-react'

import api from '@/lib/api'
import { playScanReadSound, primeScanAudio, useBarcodeScanner } from '@/lib/barcodeScanner'
import { shouldOfferCameraScan } from '@/lib/cameraScanner'
import { CameraScannerDialog } from '@/components/billing/CameraScannerDialog'
import { formatCurrency, debounce } from '@/lib/utils'
import { t } from '@/i18n'
import { useUbications, useDeleteArticle, useRestoreArticle } from '@/hooks/useArticles'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useToast } from '@/components/ui/toaster'
import type { Article } from '@/types'

export function ArticlesPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [lowStockOnly, setLowStockOnly] = useState(false)
  const [missingCabysOnly, setMissingCabysOnly] = useState(false)
  const [sortBy, setSortBy] = useState<'code' | 'description' | 'ubication' | 'count_articles'>('description')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [ubicationFilter, setUbicationFilter] = useState<string>('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [cameraAvailable] = useState(shouldOfferCameraScan)
  const [showCamera, setShowCamera] = useState(false)
  const [typing, setTyping] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<Article | null>(null)
  const deleteArticle = useDeleteArticle()
  const restoreArticle = useRestoreArticle()

  const { data: ubications = [] } = useUbications()

  // Read filter from URL on mount
  useEffect(() => {
    const lowStock = searchParams.get('low_stock')
    const missingCabys = searchParams.get('missing_cabys')
    if (lowStock === 'true') {
      setLowStockOnly(true)
    }
    if (missingCabys === 'true') {
      setMissingCabysOnly(true)
    }
  }, [searchParams])

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
      setPage(1) // Reset to first page when searching
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Lo leído reemplaza lo que hubiera escrito en el buscador, no se le pega al final.
  useBarcodeScanner((code) => handleSearchChange(code.trim()))

  const handleCameraCode = (code: string) => {
    playScanReadSound()
    handleSearchChange(code.trim())
    return true
  }

  const openCamera = () => {
    primeScanAudio()
    setShowCamera(true)
  }

  // El botón flotante se esconde mientras se escribe: con el teclado abierto tapa los campos.
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) =>
      setTyping(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
    const onFocusOut = () => setTyping(false)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  // Fetch articles
  const { data, isLoading } = useQuery({
    queryKey: ['articles', page, searchQuery, lowStockOnly, missingCabysOnly, ubicationFilter, sortBy, sortOrder, showDeleted],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', page.toString())
      params.set('page_size', '20')
      if (searchQuery) {
        params.set('search', searchQuery)
      }
      if (lowStockOnly) {
        params.set('low_stock_only', 'true')
      }
      if (missingCabysOnly) {
        params.set('missing_cabys', 'true')
      }
      if (ubicationFilter) {
        params.set('ubication_id', ubicationFilter)
      }
      if (showDeleted) {
        params.set('show_deleted', 'true')
      }
      params.set('sort_by', sortBy)
      params.set('sort_order', sortOrder)
      const response = await api.get(`/articles/?${params}`)
      return response.data
    },
  })

  return (
    <div className="space-y-4">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">{t('articles.title')}</h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {data?.total || 0} {t('articles.title').toLowerCase()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/articles/labels')}>
            <Tags className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{t('labels.open')}</span>
            <span className="sr-only md:hidden">{t('labels.open')}</span>
          </Button>
          <Button onClick={() => navigate('/articles/new')} className="hidden md:flex">
            <Plus className="h-4 w-4 mr-2" />
            {t('articles.newArticle')}
          </Button>
        </div>
      </div>

      {/* Search - Touch friendly */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder={t('articles.searchPlaceholder')}
          className="pl-10 h-12 text-base"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Filtros y orden: en el celular se desplazan de lado; desde tablet bajan a otro renglón */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none md:flex-wrap md:overflow-visible">
        <Button
          variant={missingCabysOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMissingCabysOnly(!missingCabysOnly); setPage(1) }}
          className={`shrink-0 ${missingCabysOnly ? 'bg-yellow-600 hover:bg-yellow-700' : ''}`}
        >
          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          {t('articles.missingCabys')}
        </Button>
        <Button
          variant={lowStockOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setLowStockOnly(!lowStockOnly); setPage(1) }}
          className={`shrink-0 ${lowStockOnly ? 'bg-red-600 hover:bg-red-700' : ''}`}
        >
          {t('articles.lowStockOnly')}
        </Button>
        <Button
          variant={showDeleted ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setShowDeleted(!showDeleted); setPage(1) }}
          className="shrink-0"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" />
          {t('articles.showDeleted')}
        </Button>

        <div className="shrink-0 hidden sm:block">
          <SearchableSelect
            options={[
              { value: 'all', label: t('articles.allLocations') },
              ...ubications.map((u) => ({ value: String(u.id), label: u.name })),
            ]}
            value={ubicationFilter || 'all'}
            onValueChange={(value) => { setUbicationFilter(value === 'all' ? '' : value); setPage(1) }}
            placeholder={t('articles.allLocations')}
            searchPlaceholder={t('common.search')}
            className="w-[180px] md:w-[220px] h-8 md:h-9 text-sm"
          />
        </div>

        <div className="shrink-0 flex items-center gap-1 ml-auto">
          <span className="text-xs text-muted-foreground mr-1 hidden sm:inline">{t('common.sortBy')}:</span>
          <Button variant={sortBy === 'code' ? 'default' : 'outline'} size="sm" className="shrink-0 h-7 px-2 text-xs md:h-9 md:px-3 md:text-sm"
            onClick={() => { setSortBy('code'); setPage(1) }}>{t('articles.code')}</Button>
          <Button variant={sortBy === 'description' ? 'default' : 'outline'} size="sm" className="shrink-0 h-7 px-2 text-xs md:h-9 md:px-3 md:text-sm"
            onClick={() => { setSortBy('description'); setPage(1) }}>{t('articles.description')}</Button>
          <Button variant={sortBy === 'ubication' ? 'default' : 'outline'} size="sm" className="shrink-0 h-7 px-2 text-xs md:h-9 md:px-3 md:text-sm"
            onClick={() => { setSortBy('ubication'); setPage(1) }}>{t('articles.location')}</Button>
          <Button variant={sortBy === 'count_articles' ? 'default' : 'outline'} size="sm" className="shrink-0 h-7 px-2 text-xs md:h-9 md:px-3 md:text-sm"
            onClick={() => { setSortBy('count_articles'); setPage(1) }}>{t('articles.stock')}</Button>
          <Button variant="outline" size="sm" className="shrink-0 h-7 w-7 p-0 md:h-9 md:w-9"
            onClick={() => { setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); setPage(1) }}>
            {sortOrder === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Mobile-only location filter */}
      <div className="sm:hidden">
        <SearchableSelect
          options={[
            { value: 'all', label: t('articles.allLocations') },
            ...ubications.map((u) => ({ value: String(u.id), label: u.name })),
          ]}
          value={ubicationFilter || 'all'}
          onValueChange={(value) => { setUbicationFilter(value === 'all' ? '' : value); setPage(1) }}
          placeholder={t('articles.allLocations')}
          searchPlaceholder={t('common.search')}
          className="w-full h-10 text-sm"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{t('articles.noArticles')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery ? t('common.noResults') : t('articles.addFirst')}
          </p>
          {!searchQuery && (
            <Button onClick={() => navigate('/articles/new')}>
              <Plus className="h-4 w-4 mr-2" />
              {t('articles.newArticle')}
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Tarjetas hasta 1279 px: en una tablet horizontal la tabla no cabe */}
          <div className="space-y-3 xl:hidden">
            {data?.items?.map((article: Article) => {
              const isLowStock = article.count_articles <= article.minimum_stock
              const hasCabys = !!article.cabys_code
              return (
                <Card
                  key={article.id}
                  className={`active:bg-muted/50 touch-manipulation ${article.deleted_at ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-4">
                    <Link
                      to={`/articles/${article.id}`}
                      className="flex items-center justify-between rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p
                            className="font-medium truncate cursor-copy"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              navigator.clipboard.writeText(article.code)
                              toast({ title: `${article.code} - ${t('common.copied')}` })
                            }}
                          >
                            {article.code}
                          </p>
                          {isLowStock && (
                            <AlertTriangle className="h-4 w-4 text-destructive shrink-0" aria-label={t('articles.lowStock')} />
                          )}
                        </div>
                        <p
                          className="text-sm text-muted-foreground truncate select-text"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                        >
                          {article.description}
                        </p>
                        {article.alternative_code && (
                          <p className="text-xs text-muted-foreground/70 truncate">
                            {t('articles.alternativeCode')}: {article.alternative_code}
                          </p>
                        )}
                        {article.ubication_name && (
                          <p className="text-xs text-muted-foreground/70 truncate">
                            {article.ubication_name}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="font-bold text-primary">
                            {formatCurrency(article.sale_price_1_iva)}
                          </p>
                          <p className={`text-xs ${isLowStock ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {t('articles.stock')}: {article.count_articles}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                      </div>
                    </Link>
                    {/* Eliminar vive en el detalle del artículo: en la lista quedaba al alcance del pulgar */}
                    {(article.deleted_at || !hasCabys) && (
                    <div className="flex items-center justify-between mt-2 pt-2 border-t">
                      {article.deleted_at ? (
                        <div className="flex items-center justify-between w-full">
                          <Badge variant="secondary" className="text-xs">{t('articles.articleDeleted')}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 text-xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              restoreArticle.mutate(article.id)
                            }}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            {t('articles.restoreArticle')}
                          </Button>
                        </div>
                      ) : !hasCabys ? (
                        <>
                          <Badge variant="warning">
                            <AlertTriangle className="h-3 w-3 mr-1" aria-hidden="true" />
                            {t('articles.cabysWarning')}
                          </Badge>
                          <Button variant="ghost" size="sm" className="h-9 text-xs" asChild>
                            <Link to={`/articles/${article.id}/cabys`}>
                              <Pencil className="h-3 w-3 mr-1" aria-hidden="true" />
                              {t('articles.updateCabys')}
                            </Link>
                          </Button>
                        </>
                      ) : null}
                    </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden xl:block">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium">{t('articles.code')}</th>
                    <th className="text-left p-4 font-medium">{t('articles.description')}</th>
                    <th className="text-left p-4 font-medium">{t('articles.location')}</th>
                    <th className="text-left p-4 font-medium">{t('articles.cabysCode')}</th>
                    <th className="text-right p-4 font-medium">{t('articles.stock')}</th>
                    <th className="text-right p-4 font-medium">{t('articles.price')}</th>
                    <th className="p-4 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((article: Article) => {
                    const isLowStock = article.count_articles <= article.minimum_stock
                    const hasCabys = !!article.cabys_code
                    return (
                      <tr
                        key={article.id}
                        className={`group border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${article.deleted_at ? 'opacity-60' : ''}`}
                        onClick={() => navigate(`/articles/${article.id}`)}
                        tabIndex={0}
                        aria-label={`${article.code} ${article.description}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/articles/${article.id}`)
                        }}
                      >
                        <td
                          className="p-4 cursor-copy"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(article.code)
                            toast({ title: `${article.code} - ${t('common.copied')}` })
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{article.code}</span>
                            <Copy className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            {isLowStock && (
                              <Badge variant="destructive" className="text-xs">
                                {t('articles.lowStock')}
                              </Badge>
                            )}
                          </div>
                          {article.alternative_code && (
                            <span className="text-xs text-muted-foreground block">
                              {t('articles.alternativeCode')}: {article.alternative_code}
                            </span>
                          )}
                        </td>
                        <td
                          className="p-4 select-text"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {article.description}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {article.ubication_name || '-'}
                        </td>
                        <td className="p-4">
                          {hasCabys ? (
                            <span className="text-sm font-mono">{article.cabys_code}</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Badge variant="warning">
                                <AlertTriangle className="h-3 w-3 mr-1" aria-hidden="true" />
                                {t('articles.cabysWarning')}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={t('articles.updateCabys')}
                                title={t('articles.updateCabys')}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/articles/${article.id}/cabys`)
                                }}
                              >
                                <Pencil className="h-3 w-3" aria-hidden="true" />
                              </Button>
                            </div>
                          )}
                        </td>
                        <td className={`p-4 text-right ${isLowStock ? 'text-destructive font-medium' : ''}`}>
                          {article.count_articles}
                        </td>
                        <td className="p-4 text-right font-medium text-primary">
                          {formatCurrency(article.sale_price_1_iva)}
                        </td>
                        <td className="p-4">
                          {article.deleted_at ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              aria-label={t('articles.restoreArticle')}
                              title={t('articles.restoreArticle')}
                              onClick={(e) => {
                                e.stopPropagation()
                                restoreArticle.mutate(article.id)
                              }}
                            >
                              <RotateCcw className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                              aria-label={t('common.delete')}
                              title={t('common.delete')}
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteTarget(article)
                              }}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {data.page} {t('common.of')} {data.pages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('articles.deleteArticle')}: {deleteTarget?.code}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{deleteTarget?.description}</p>
                <p>{t('articles.confirmDeleteSoft')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteTarget) {
                  await deleteArticle.mutateAsync({ id: deleteTarget.id })
                  setDeleteTarget(null)
                }
              }}
            >
              {t('articles.deactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* En el celular va encima del botón de nuevo artículo */}
      {cameraAvailable && !typing && !showCamera && (
        <Button
          className="fixed right-4 z-40 h-14 w-14 rounded-full shadow-lg bottom-[calc(9.5rem+env(safe-area-inset-bottom))] md:bottom-6"
          onClick={openCamera}
          aria-label={t('pos.cameraScan')}
        >
          <ScanBarcode className="h-6 w-6" aria-hidden="true" />
        </Button>
      )}

      <CameraScannerDialog open={showCamera} onOpenChange={setShowCamera} onCode={handleCameraCode} />
    </div>
  )
}
